import { useState, useEffect, useRef, useCallback } from 'react';
import { formatCurrency } from '../utils/formatters';
import './ClientDashboard.css';

const API = '/api';

const Icon = ({ d, size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    building: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
    mail: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
    phone: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z',
    map: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z', 'M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    briefcase: ['M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z', 'M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2'],
    logout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
    check: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3',
    trips: ['M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3', 'M14 17H9m10 0h3m-3 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm1-10h.01'],
    invoice: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
    analytics: ['M3 3v18h18', 'M18 17l-6-6-4 4-4-4'],
    staff: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    chevronDown: 'M6 9l6 6 6-6',
    chevronUp: 'M18 15l-6-6-6 6',
    fileText: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8'],
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'm7 10 5 5 5-5', 'M12 15V3'],
    bell: ['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0'],
    alertCircle: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 8v4', 'M12 16h.01'],
};

export default function ClientDashboard({ client, onLogout, operatorDetails }) {
    const [trips, setTrips] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [clientUsers, setClientUsers] = useState([]);
    const [passengers, setPassengers] = useState([]);
    const [quotations, setQuotations] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [loadingTrips, setLoadingTrips] = useState(true);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    const [loadingQuotations, setLoadingQuotations] = useState(true);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [loadingPassengers, setLoadingPassengers] = useState(true);
    const [loadingRoutes, setLoadingRoutes] = useState(true);
    const [activeTab, setActiveTab] = useState('analytics');
    const [financeMenuOpen, setFinanceMenuOpen] = useState(true);
    const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
    const [countries, setCountries] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [showNotifPanel, setShowNotifPanel] = useState(false);

    useEffect(() => {
        fetch(`${API}/countries`)
            .then(r => r.json())
            .then(setCountries)
            .catch(err => console.error("Error fetching countries:", err));
    }, []);

    // Load client notifications
    const loadNotifications = useCallback(() => {
        if (!client?._id) return;
        fetch(`${API}/notifications/client/${client._id}`)
            .then(r => r.json())
            .then(data => setNotifications(Array.isArray(data) ? data : []))
            .catch(err => console.error('Error loading notifications:', err));
    }, [client]);

    useEffect(() => {
        loadNotifications();
        // Poll every 30 seconds for new notifications
        const interval = setInterval(loadNotifications, 30000);
        return () => clearInterval(interval);
    }, [loadNotifications]);

    const loadStaffData = useCallback(async () => {
        if (!client?._id) return;
        setLoadingPassengers(true);
        try {
            // Fetch both contacts and passengers in parallel
            const [pRes, clientRes] = await Promise.all([
                fetch(`${API}/passengers/${client.operatorId}`),
                fetch(`${API}/operators/${client.operatorId}/clients/${client._id}`)
            ]);

            const pData = await pRes.json();
            const myPassengers = (Array.isArray(pData) ? pData : []).filter(p => (p.clientId?._id || p.clientId) === client._id);

            let currentClient = client;
            if (clientRes.ok) {
                const refreshedClient = await clientRes.json();
                if (refreshedClient) currentClient = refreshedClient;
            }

            const contacts = (currentClient.contacts || []).map(c => ({
                _id: Number.isSafeInteger(Number(c._id)) ? String(c._id) : (c._id ? String(c._id) : null), // Handle both ObjectId and indexed IDs
                firstName: c.firstName,
                lastName: c.lastName,
                name: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
                email: c.email,
                primaryPhoneCode: c.primaryPhoneCode,
                primaryPhoneNumber: c.primaryPhoneNumber,
                contactNumber: c.primaryPhoneNumber, // Legacy support
                alternatePhoneCode: c.alternatePhoneCode,
                alternatePhoneNumber: c.alternatePhoneNumber,
                profileImage: c.profileImage,
                homeLocation: c.homeLocation,
                workLocation: c.workLocation,
                pickupLocation: c.homeLocation, // Legacy support
                dropLocation: c.workLocation,   // Legacy support
                employeeId: c._id ? String(c._id).slice(-10) : 'STAFF',
                source: 'contact'
            }));

            // FINAL DEDUPLICATION: Use a temporary object to hold unique people by Name
            const deduplicated = {};

            // 1. Process database passengers (they have real trip history potential)
            myPassengers.forEach(p => {
                // Split name if firstName/lastName missing
                let fN = p.firstName;
                let lN = p.lastName;
                if (!fN && p.name) {
                    const parts = p.name.split(' ');
                    fN = parts[0];
                    lN = parts.slice(1).join(' ');
                }

                const mapped = {
                    ...p,
                    _id: String(p._id),
                    firstName: fN || '',
                    lastName: lN || '',
                    primaryPhoneNumber: p.contactNumber || p.primaryPhoneNumber,
                    homeLocation: p.pickupLocation || p.homeLocation,
                    workLocation: p.dropLocation || p.workLocation,
                    source: 'db'
                };
                const key = (p.name || '').toLowerCase() || (mapped.firstName + mapped.lastName).toLowerCase();
                if (key) deduplicated[key] = mapped;
            });

            // 2. Process contacts (overwriting or adding missing ones)
            contacts.forEach(c => {
                const key = (c.name || '').toLowerCase();
                if (key) {
                    if (deduplicated[key]) {
                        // Merge contact info into existing passenger info if missing
                        deduplicated[key] = { ...c, ...deduplicated[key], source: 'merged' };
                    } else {
                        deduplicated[key] = { ...c, source: 'contact' };
                    }
                }
            });

            setPassengers(Object.values(deduplicated));
        } catch (err) {
            console.error("Staff load error:", err);
        } finally {
            setLoadingPassengers(false);
        }
    }, [client]);

    const loadTrips = useCallback(() => {
        if (!client?.operatorId) return;
        setLoadingTrips(true);
        fetch(`${API}/trips/${client.operatorId}`)
            .then(r => r.json())
            .then(data => {
                setTrips((Array.isArray(data) ? data : []).filter(t => (t.clientId?._id || t.clientId) === client._id));
            })
            .catch(err => console.error("Error loading trips:", err))
            .finally(() => setLoadingTrips(false));
    }, [client]);

    useEffect(() => {
        if (!client?.operatorId) return;

        loadTrips();

        // Load invoices
        fetch(`${API}/invoices/client/${client._id}`)
            .then(r => r.json())
            .then(data => {
                setInvoices(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error("Error loading invoices:", err))
            .finally(() => setLoadingInvoices(false));

        // Load client users
        fetch(`${API}/client-users/${client._id}`)
            .then(r => r.json())
            .then(data => {
                setClientUsers(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error("Error loading client users:", err))
            .finally(() => setLoadingUsers(false));

        // Load quotations
        fetch(`${API}/quotations/client/${client._id}`)
            .then(r => r.json())
            .then(data => {
                setQuotations(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error("Error loading quotations:", err))
            .finally(() => setLoadingQuotations(false));

        // Load routes
        fetch(`${API}/routes/${client.operatorId}`)
            .then(r => r.json())
            .then(data => {
                setRoutes(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error("Error loading routes:", err))
            .finally(() => setLoadingRoutes(false));

        loadStaffData();
    }, [client, loadStaffData, loadTrips]);

    const loadUsers = useCallback(() => {
        setLoadingUsers(true);
        fetch(`${API}/client-users/${client._id}`)
            .then(r => r.json())
            .then(data => setClientUsers(Array.isArray(data) ? data : []))
            .catch(err => console.error("Error loading users:", err))
            .finally(() => setLoadingUsers(false));
    }, [client]);

    const activeTrips = trips.filter(t => t.status !== 'Completed' && t.status !== 'Cancelled');
    const completedTrips = trips.filter(t => t.status === 'Completed');
    const pendingInvoices = invoices.filter(i => i.status !== 'Paid');
    const totalInvoiced = invoices.reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0);

    const statCards = [
        { label: 'Active Trips', value: loadingTrips ? '…' : activeTrips.length, icon: icons.trips, color: '#334155', bg: 'rgba(51, 65, 85, 0.08)', shadow: 'rgba(51, 65, 85, 0.2)' },
        { label: 'Completed Trips', value: loadingTrips ? '…' : completedTrips.length, icon: icons.check, color: '#059669', bg: 'rgba(5, 150, 105, 0.08)', shadow: 'rgba(5, 150, 105, 0.2)' },
        { label: 'Pending Invoices', value: loadingInvoices ? '…' : pendingInvoices.length, icon: icons.invoice, color: '#d97706', bg: 'rgba(217, 119, 6, 0.08)', shadow: 'rgba(217, 119, 6, 0.2)' },
        { label: 'Total Invoiced', value: loadingInvoices ? '…' : formatCurrency(totalInvoiced, client.country), icon: icons.briefcase, color: '#0f172a', bg: 'rgba(15, 23, 42, 0.08)', shadow: 'rgba(15, 23, 42, 0.2)' },
    ];

    return (
        <div className="cd-shell" onClick={() => showNotifPanel && setShowNotifPanel(false)}>
            <aside className="cd-sidebar">
                <div className="cd-sidebar-logo">
                    <div className="cd-header-logo">
                        {operatorDetails?.companyLogo ? (
                            <img
                                src={`${API}/uploads/${operatorDetails.companyLogo}`}
                                alt={operatorDetails?.companyName}
                                style={{ width: '100%', height: '100%', borderRadius: 8, objectFit: 'contain' }}
                            />
                        ) : (
                            <Icon d={icons.building} size={20} color="white" />
                        )}
                    </div>
                    <div className="cd-header-brand-container">
                        <div className="cd-header-brand">{operatorDetails?.companyName || 'Opsmaticz'}</div>
                        <div className="cd-header-sub">Client Portal</div>
                    </div>
                </div>
                <nav className="cd-sidebar-nav">
                    <div className={`cd-nav-item ${activeTab === 'analytics' ? "active" : ""}`} onClick={() => setActiveTab('analytics')}>
                        <Icon d={icons.analytics} size={18} color="#666666" /><span>Analytics</span>
                    </div>
                    <div className={`cd-nav-item ${activeTab === 'staff' ? "active" : ""}`} onClick={() => setActiveTab('staff')}>
                        <Icon d={icons.staff} size={18} color="#666666" /><span>Passengers</span>
                    </div>
                    <div className={`cd-nav-item ${activeTab === 'routes' ? "active" : ""}`} onClick={() => setActiveTab('routes')}>
                        <Icon d={icons.map} size={18} color="#666666" /><span>Routes</span>
                    </div>
                    <div className={`cd-nav-item ${activeTab === 'request_trip' ? "active" : ""}`} onClick={() => setActiveTab('request_trip')}>
                        <Icon d={icons.plus} size={18} color="#666666" /><span>Request Service</span>
                    </div>
                    <div className={`cd-nav-item ${activeTab === 'trips' ? "active" : ""}`} onClick={() => setActiveTab('trips')}>
                        <Icon d={icons.trips} size={18} color="#666666" /><span>Trips</span>
                    </div>
                    <div className="cd-nav-group">
                        <div className={`cd-nav-item ${(activeTab === 'finance' || activeTab === 'quotations') && !financeMenuOpen ? "active" : ""}`} onClick={() => setFinanceMenuOpen(!financeMenuOpen)}>
                            <Icon d={icons.invoice} size={18} color="#666666" />
                            <span style={{ flex: 1 }}>Finance</span>
                            <Icon d={financeMenuOpen ? icons.chevronUp : icons.chevronDown} size={14} color="#999999" />
                        </div>
                        {financeMenuOpen && (
                            <div className="cd-submenu">
                                <div className={`cd-submenu-item ${activeTab === 'finance' ? "active" : ""}`} onClick={() => setActiveTab('finance')}>
                                    <Icon d={icons.invoice} size={14} color={activeTab === 'finance' ? '#0f172a' : '#999999'} />
                                    <span>Invoices</span>
                                </div>
                                <div className={`cd-submenu-item ${activeTab === 'quotations' ? "active" : ""}`} onClick={() => setActiveTab('quotations')}>
                                    <Icon d={icons.invoice} size={14} color={activeTab === 'quotations' ? '#0f172a' : '#999999'} />
                                    <span>Quotations</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="cd-nav-group">
                        <div className={`cd-nav-item ${(activeTab === 'reports_trips' || activeTab === 'reports_finance') && !reportsMenuOpen ? "active" : ""}`} onClick={() => setReportsMenuOpen(!reportsMenuOpen)}>
                            <Icon d={icons.fileText} size={18} color="#666666" />
                            <span style={{ flex: 1 }}>Reports</span>
                            <Icon d={reportsMenuOpen ? icons.chevronUp : icons.chevronDown} size={14} color="#999999" />
                        </div>
                        {reportsMenuOpen && (
                            <div className="cd-submenu">
                                <div className={`cd-submenu-item ${activeTab === 'reports_trips' ? "active" : ""}`} onClick={() => setActiveTab('reports_trips')}>
                                    <Icon d={icons.trips} size={14} color={activeTab === 'reports_trips' ? '#0f172a' : '#999999'} />
                                    <span>Trip Intensity</span>
                                </div>
                                <div className={`cd-submenu-item ${activeTab === 'reports_finance' ? "active" : ""}`} onClick={() => setActiveTab('reports_finance')}>
                                    <Icon d={icons.invoice} size={14} color={activeTab === 'reports_finance' ? '#0f172a' : '#999999'} />
                                    <span>Financial Logs</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={`cd-nav-item ${activeTab === 'users' ? "active" : ""}`} onClick={() => setActiveTab('users')}>
                        <Icon d={icons.user} size={18} color="#666666" /><span>Users</span>
                    </div>
                </nav>
                <div className="cd-sidebar-bottom"><button id="client-logout-btn" onClick={onLogout} className="cd-logout-btn"><Icon d={icons.logout} size={16} /><span>Sign Out</span></button></div>
            </aside>

            <div className="cd-content-area">
                <header className="cd-header">
                    <div className="cd-header-left"><h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                        {activeTab === 'analytics' ? 'Dashboard' :
                            activeTab === 'staff' ? 'Passenger Management' :
                                activeTab === 'routes' ? 'Assigned Logistics' :
                                    activeTab === 'trips' ? 'Scheduled & Requested Trips' :
                                        activeTab === 'users' ? 'Access & User Management' :
                                            activeTab === 'finance' ? 'Financial Reports & Invoices' :
                                                activeTab === 'quotations' ? 'Commercial Quotations & Proposals' :
                                                    activeTab.startsWith('reports') ? 'Business Insight & Operations Reports' :
                                                        'Request Main Trip'}
                    </h2></div>
                    <div className="cd-header-right">
                        {/* Notification Bell */}
                        <div style={{ position: 'relative', marginRight: 12 }}>
                            <button
                                id="client-notif-btn"
                                onClick={() => setShowNotifPanel(p => !p)}
                                style={{
                                    background: notifications.filter(n => !n.isRead).length > 0 ? '#fffbeb' : '#f8fafc',
                                    border: `1px solid ${notifications.filter(n => !n.isRead).length > 0 ? '#fde68a' : '#e2e8f0'}`,
                                    cursor: 'pointer', padding: '7px 9px', position: 'relative',
                                    display: 'flex', alignItems: 'center', borderRadius: 10, transition: 'all 0.2s'
                                }}
                                title="Notifications"
                            >
                                <Icon d={icons.bell} size={18} color={notifications.filter(n => !n.isRead).length > 0 ? '#d97706' : '#6b7280'} />
                                {notifications.filter(n => !n.isRead).length > 0 && (
                                    <span style={{
                                        position: 'absolute', top: -5, right: -5,
                                        background: '#ef4444', color: 'white', borderRadius: '50%',
                                        width: 17, height: 17, fontSize: 9, fontWeight: 900,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 2px 6px rgba(239,68,68,0.5)'
                                    }}>
                                        {notifications.filter(n => !n.isRead).length > 9 ? '9+' : notifications.filter(n => !n.isRead).length}
                                    </span>
                                )}
                            </button>

                            {showNotifPanel && (
                                <div
                                    onClick={e => e.stopPropagation()}
                                    style={{
                                        position: 'fixed', top: 0, right: 0, height: '100vh', width: 360,
                                        background: '#ffffff', boxShadow: '-4px 0 32px rgba(0,0,0,0.13)',
                                        borderLeft: '1px solid #e5e7eb', zIndex: 9999,
                                        display: 'flex', flexDirection: 'column', fontFamily: 'inherit'
                                    }}
                                >
                                    {/* Panel Header */}
                                    <div style={{
                                        padding: '22px 20px 16px',
                                        background: 'linear-gradient(135deg,#f0f9ff,#e0f2fe)',
                                        borderBottom: '1px solid #bae6fd'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <div style={{ fontWeight: 800, fontSize: 16, color: '#0c4a6e', letterSpacing: '-0.02em' }}>Notifications</div>
                                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                                                    {notifications.filter(n => !n.isRead).length > 0
                                                        ? `${notifications.filter(n => !n.isRead).length} unread`
                                                        : 'All caught up!'}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                {notifications.filter(n => !n.isRead).length > 0 && (
                                                    <button
                                                        onClick={async () => {
                                                            await fetch(`${API}/notifications/mark-all-read/client/${client._id}`, { method: 'PUT' });
                                                            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
                                                        }}
                                                        style={{
                                                            fontSize: 11, color: '#0369a1', background: 'white',
                                                            border: '1px solid #bae6fd', borderRadius: 8,
                                                            cursor: 'pointer', fontWeight: 700, padding: '5px 10px'
                                                        }}
                                                    >
                                                        Mark all read
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setShowNotifPanel(false)}
                                                    style={{
                                                        background: 'white', border: '1px solid #e5e7eb',
                                                        borderRadius: 8, width: 30, height: 30,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <Icon d={icons.x} size={14} color="#6b7280" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notification List */}
                                    <div style={{ flex: 1, overflowY: 'auto' }}>
                                        {notifications.length === 0 ? (
                                            <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
                                                <Icon d={icons.bell} size={40} color="#bae6fd" />
                                                <div style={{ marginTop: 12, fontWeight: 700, fontSize: 14, color: '#374151' }}>No notifications yet</div>
                                                <div style={{ marginTop: 4, fontSize: 12 }}>You're all caught up!</div>
                                            </div>
                                        ) : notifications.map((n) => {
                                            const typeColor = (
                                                n.type === 'trip_request_accepted' ? '#10b981' :
                                                    n.type === 'trip_scheduled' ? '#6366f1' :
                                                        n.type === 'trip_request_rejected' ? '#ef4444' :
                                                            n.type === 'trip_status_update' ? '#0ea5e9' :
                                                                '#6b7280'
                                            );
                                            return (
                                                <div
                                                    key={n._id}
                                                    style={{
                                                        padding: '14px 20px', borderBottom: '1px solid #f3f4f6',
                                                        background: n.isRead ? 'white' : '#fefce8',
                                                        display: 'flex', gap: 12, alignItems: 'flex-start',
                                                        cursor: 'default'
                                                    }}
                                                >
                                                    <div style={{
                                                        width: 38, height: 38, borderRadius: 12,
                                                        background: typeColor + '18',
                                                        border: `1.5px solid ${typeColor}40`,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: typeColor }} />
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.35 }}>{n.title}</div>
                                                            {!n.isRead && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', flexShrink: 0, marginTop: 4, display: 'block' }} />}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>{n.message}</div>
                                                        <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 6, fontWeight: 500 }}>{new Date(n.createdAt).toLocaleString()}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="cd-avatar-chip"><div className="cd-avatar">{(client?.companyName || 'C').charAt(0).toUpperCase()}</div><div className="hide-mobile"><div className="cd-avatar-name">{client?.companyName}</div><div className="cd-avatar-email">{client?.email}</div></div></div>
                    </div>
                </header>

                <main className="cd-main">
                    {activeTab === 'analytics' ? (
                        <>
                            <div className="cd-welcome-banner">
                                <div style={{ zIndex: 2 }}>
                                    <h1 className="cd-welcome-h1">Welcome back, {client?.companyName}!</h1>
                                    <p className="cd-welcome-p">System status: All operations performing optimally.</p>
                                    {operatorDetails?.companyName && (
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '4px 12px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 20, backdropFilter: 'blur(8px)' }}>
                                            {operatorDetails?.companyLogo && (
                                                <img src={`/api/uploads/${operatorDetails.companyLogo}`} alt="" style={{ width: 14, height: 14, borderRadius: 3, objectFit: 'contain' }} />
                                            )}
                                            <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.02em' }}>
                                                Administered by {operatorDetails.companyName}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="cd-welcome-badge"><span>OPERATIONAL • ACTIVE</span></div>
                                <div style={{ position: 'absolute', right: '-50px', top: '-50px', width: 200, height: 200, background: 'rgba(255,255,255,0.05)', borderRadius: '50%', filter: 'blur(40px)', zIndex: 1 }}></div>
                            </div>
                            <div className="cd-stats-grid">{statCards.map(c => (
                                <div key={c.label} className="cd-stat-card" style={{ borderTop: `4px solid ${c.color}` }}>
                                    <div className="cd-stat-icon" style={{ background: c.bg }}><Icon d={c.icon} size={22} color={c.color} /></div>
                                    <div className="cd-stat-value" style={{ color: c.color }}>
                                        {typeof c.value === 'string' && c.value.includes('KWD') ? (
                                            <>
                                                <span style={{ fontSize: '0.6em', fontWeight: 700, marginRight: 4, opacity: 0.8 }}>KWD</span>
                                                {c.value.split('.')[0].replace('KWD ', '')}
                                                {c.value.includes('.') && <span style={{ fontSize: '0.7em', opacity: 0.8 }}>.{c.value.split('.')[1]}</span>}
                                            </>
                                        ) : c.value}
                                    </div>
                                    <div className="cd-stat-label">{c.label}</div>
                                    <div style={{ position: 'absolute', bottom: -10, right: -10, fontSize: 40, opacity: 0.03, color: c.color }}><Icon d={c.icon} size={60} /></div>
                                </div>
                            ))}</div>
                            <div className="cd-two-col">
                                <div className="cd-card"><div className="cd-card-header"><Icon d={icons.user} size={18} color="#6366f1" /><span className="cd-card-title">Account Info</span></div>
                                    <div className="cd-info-list"><InfoRow icon={icons.building} label="Company" value={client?.companyName} /><InfoRow icon={icons.user} label="Contact" value={client?.contactPerson} /><InfoRow icon={icons.mail} label="Email" value={client?.email} /><InfoRow icon={icons.phone} label="Phone" value={client?.phone} /></div>
                                </div>
                                <div className="cd-card"><div className="cd-card-header"><Icon d={icons.trips} size={18} color="#6366f1" /><span className="cd-card-title">Recent Trips</span></div>
                                    {loadingTrips ? <div className="cd-empty">Loading...</div> : trips.length === 0 ? <div className="cd-empty">No trips.</div> : <div className="cd-trip-list">{trips.slice(0, 5).map(t => (
                                        <div key={t._id} className="cd-trip-row">
                                            <div>
                                                <div className="cd-trip-name">{t.passengerName || t.name || t.routeName}</div>
                                                <div className="cd-trip-date">{t.tripDate ? new Date(t.tripDate).toLocaleDateString() : '—'}</div>
                                            </div>
                                            <span className="cd-badge" style={{
                                                display: 'inline-block',
                                                background: t.status === 'Scheduled' ? '#dcfce7' : t.status === 'Requested' ? '#fef3c7' : t.status === 'Rejected' ? '#fee2e2' : '#f1f5f9',
                                                color: t.status === 'Scheduled' ? '#166534' : t.status === 'Requested' ? '#92400e' : t.status === 'Rejected' ? '#991b1b' : '#475569'
                                            }}>
                                                {t.status}
                                            </span>
                                        </div>
                                    ))}</div>}
                                </div>
                            </div>
                        </>
                    ) : activeTab === 'staff' ? (
                        <PassengersSection client={client} passengers={passengers} loading={loadingPassengers} onRefresh={loadStaffData} countries={countries} />
                    ) : activeTab === 'routes' ? (
                        <RoutesSection routes={routes} loading={loadingRoutes} />
                    ) : activeTab === 'trips' ? (
                        <TripsSection trips={trips} loading={loadingTrips} client={client} onRefresh={loadTrips} />
                    ) : activeTab === 'users' ? (
                        <UsersSection client={client} users={clientUsers} loading={loadingUsers} onRefresh={loadUsers} />
                    ) : activeTab === 'request_trip' ? (
                        <TripRequestSection client={client} routes={routes} onComplete={() => setActiveTab('analytics')} />
                    ) : activeTab === 'finance' ? (
                        <FinanceSection invoices={invoices} trips={trips} routes={routes} country={client.country} loading={loadingInvoices} />
                    ) : activeTab === 'quotations' ? (
                        <QuotationsSection quotations={quotations} country={client.country} loading={loadingQuotations} />
                    ) : activeTab === 'reports_trips' ? (
                        <ReportsSection type="trips" data={trips} country={client.country} />
                    ) : activeTab === 'reports_finance' ? (
                        <ReportsSection type="finance" data={invoices} country={client.country} />
                    ) : null}
                </main>
            </div>
        </div>
    );
}

function PassengersSection({ client, passengers, loading, onRefresh, countries }) {
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null);

    const handleSave = async (formData) => {
        try {
            const isEdit = !!modal.data?._id;
            // Sync with Passenger collection
            const passengerPayload = {
                ...formData,
                clientId: client._id,
                operatorId: client.operatorId,
                pickupLocation: formData.homeLocation, // Sync for trip ops
                dropLocation: formData.workLocation,
                contactNumber: formData.primaryPhoneNumber
            };

            let pRes;
            if (isEdit) {
                pRes = await fetch(`${API}/passengers/${modal.data._id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(passengerPayload) });
            } else {
                pRes = await fetch(`${API}/passengers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(passengerPayload) });
            }

            if (pRes.ok) { setModal(null); onRefresh(); } else alert("Error saving passenger.");
        } catch (err) { console.error(err); alert("Network error."); }
    };

    const filtered = passengers.filter(p => (p.name || '').toLowerCase().includes(search.toLowerCase()) || (p.employeeId || '').toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="cd-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                <div className="cd-search-wrap"><Icon d={icons.search} size={16} color="#94a3b8" /><input className="cd-search-input" placeholder="Search passengers..." value={search} onChange={e => setSearch(e.target.value)} /></div>
                <button className="cd-add-btn" onClick={() => setModal({ data: null })}><Icon d={icons.plus} size={16} color="white" /><span>Add Passenger</span></button>
            </div>
            {loading ? <div className="cd-empty">Loading...</div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="cd-table">
                        <thead><tr><th className="cd-th">Name</th><th className="cd-th">ID</th><th className="cd-th">Contact</th><th className="cd-th">Locations</th><th className="cd-th">Actions</th></tr></thead>
                        <tbody>
                            {filtered.map(p => (
                                <tr key={p._id || p.name} className="cd-tr">
                                    <td className="cd-td" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        {p.profileImage ? (
                                            <img src={`${API}/uploads/${p.profileImage}`} style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover' }} alt="" />
                                        ) : (
                                            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Icon d={icons.user} size={16} color="#94a3b8" />
                                            </div>
                                        )}
                                        <strong>{p.firstName || ''} {p.lastName || ''}</strong>
                                        {!(p.firstName || p.lastName) && p.name && <strong>{p.name}</strong>}
                                    </td>
                                    <td className="cd-td">{p.employeeId}</td><td className="cd-td">{p.primaryPhoneNumber || p.contactNumber || p.email}</td>
                                    <td className="cd-td"><div style={{ fontSize: 11 }}>H: {p.homeLocation || p.pickupLocation || '—'}</div><div style={{ fontSize: 11 }}>W: {p.workLocation || p.dropLocation || '—'}</div></td>
                                    <td className="cd-td"><div style={{ display: 'flex', gap: 6 }}><button className="cd-icon-btn" onClick={() => setModal({ data: p })}><Icon d={icons.edit} size={14} color="#6366f1" /></button>
                                        <button className="cd-icon-btn" style={{ background: '#fee2e2' }} onClick={async () => { if (window.confirm(`Delete ${p.name}?`)) { await fetch(`${API}/passengers/${p._id}`, { method: 'DELETE' }); onRefresh(); } }}><Icon d={icons.trash} size={14} color="#ef4444" /></button>
                                    </div></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {modal && <PassengerForm data={modal.data} onClose={() => setModal(null)} onSave={handleSave} countries={countries} />}
        </div>
    );
}

function PassengerForm({ data, onClose, onSave, countries }) {
    const [form, setForm] = useState(() => {
        if (data) {
            return {
                ...data,
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                primaryPhoneNumber: data.primaryPhoneNumber || data.contactNumber || '',
                homeLocation: data.homeLocation || data.pickupLocation || '',
                workLocation: data.workLocation || data.dropLocation || '',
                primaryPhoneCode: data.primaryPhoneCode || '+971'
            };
        }
        return {
            firstName: '', lastName: '', name: '', employeeId: '',
            primaryPhoneNumber: '', primaryPhoneCode: '+971',
            alternatePhoneCode: '', alternatePhoneNumber: '',
            email: '', homeLocation: '', workLocation: '',
            status: 'Active', profileImage: ''
        };
    });
    const [uploading, setUploading] = useState(false);
    const pRef = useRef(); const dRef = useRef();

    useEffect(() => {
        if (!window.google) return;
        const pA = new window.google.maps.places.Autocomplete(pRef.current);
        const dA = new window.google.maps.places.Autocomplete(dRef.current);
        pA.addListener('place_changed', () => setForm(f => ({ ...f, homeLocation: pA.getPlace().formatted_address })));
        dA.addListener('place_changed', () => setForm(f => ({ ...f, workLocation: dA.getPlace().formatted_address })));
    }, []);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('document', file);
        try {
            const res = await fetch(`${API}/api/upload`, { method: 'POST', body: formData });
            const result = await res.json();
            if (res.ok) setForm(f => ({ ...f, profileImage: result.filename }));
        } catch (err) { console.error(err); } finally { setUploading(false); }
    };

    const handleSaveLocal = () => {
        const fullForm = { ...form, name: `${form.firstName || ''} ${form.lastName || ''}`.trim() };
        onSave(fullForm);
    };

    return (
        <ModalOverlay onClose={onClose} title={data?._id ? 'Update Stakeholder' : 'Register New Contact'}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Profile Picture Attachment</label>
                    <div style={{ border: '2px dashed #e2e8f0', borderRadius: 14, padding: 16, textAlign: 'center', background: '#f8fafc', position: 'relative', cursor: 'pointer' }}>
                        {form.profileImage ? (
                            <div style={{ textAlign: 'center' }}>
                                <img src={`${API}/uploads/${form.profileImage}`} style={{ width: 64, height: 64, borderRadius: 18, objectFit: 'cover', margin: '0 auto 8px', display: 'block', border: '3px solid #f1f5f9' }} alt="" />
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#4f46e5' }}>Change Image</div>
                            </div>
                        ) : (
                            <>
                                <Icon d={icons.user} size={20} color="#94a3b8" />
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginTop: 8 }}>{uploading ? 'Processing File...' : 'Click to Upload Avatar'}</div>
                            </>
                        )}
                        <input type="file" onChange={handleFileChange} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} accept="image/*" />
                    </div>
                </div>

                <div style={{ gridColumn: 'span 1' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>First Name</label>
                    <div className="cd-input-wrap"><Icon d={icons.user} size={18} color="#94a3b8" className="cd-input-icon" /><input className="cd-modal-input-icon" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} placeholder="John" /></div>
                </div>
                <div style={{ gridColumn: 'span 1' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Last Name</label>
                    <div className="cd-input-wrap"><Icon d={icons.user} size={18} color="#94a3b8" className="cd-input-icon" /><input className="cd-modal-input-icon" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} placeholder="Doe" /></div>
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Direct Email Communication</label>
                    <div className="cd-input-wrap"><Icon d={icons.mail} size={18} color="#94a3b8" className="cd-input-icon" /><input className="cd-modal-input-icon" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="stakeholder@company.com" /></div>
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Primary Communication Number</label>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <select className="cd-modal-input" style={{ width: 110 }} value={form.primaryPhoneCode} onChange={e => setForm({ ...form, primaryPhoneCode: e.target.value })}>
                            {countries.map(c => <option key={c._id} value={c.code}>{c.code}</option>)}
                        </select>
                        <div className="cd-input-wrap" style={{ flex: 1 }}><Icon d={icons.phone} size={18} color="#94a3b8" className="cd-input-icon" /><input className="cd-modal-input-icon" value={form.primaryPhoneNumber} onChange={e => setForm({ ...form, primaryPhoneNumber: e.target.value })} placeholder="Primary Number" /></div>
                    </div>
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Alternate Contact / Phone</label>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <select className="cd-modal-input" style={{ width: 110 }} value={form.alternatePhoneCode} onChange={e => setForm({ ...form, alternatePhoneCode: e.target.value })}>
                            <option value="">Code</option>
                            {countries.map(c => <option key={c._id} value={c.code}>{c.code}</option>)}
                        </select>
                        <div className="cd-input-wrap" style={{ flex: 1 }}><Icon d={icons.phone} size={18} color="#94a3b8" className="cd-input-icon" /><input className="cd-modal-input-icon" value={form.alternatePhoneNumber} onChange={e => setForm({ ...form, alternatePhoneNumber: e.target.value })} placeholder="Secondary phone" /></div>
                    </div>
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Home Location (Personal)</label>
                    <div className="cd-input-wrap"><Icon d={icons.map} size={18} color="#94a3b8" className="cd-input-icon" /><input ref={pRef} className="cd-modal-input-icon" value={form.homeLocation} onChange={e => setForm({ ...form, homeLocation: e.target.value })} placeholder="Personal address" /></div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Work Location (Assigned)</label>
                    <div className="cd-input-wrap"><Icon d={icons.briefcase} size={18} color="#94a3b8" className="cd-input-icon" /><input ref={dRef} className="cd-modal-input-icon" value={form.workLocation} onChange={e => setForm({ ...form, workLocation: e.target.value })} placeholder="Work site / branch Office" /></div>
                </div>

                <div style={{ gridColumn: 'span 2', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Status</label>
                        <select className="cd-modal-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Passenger ID</label>
                        <input className="cd-modal-input" value={form.employeeId} onChange={e => setForm({ ...form, employeeId: e.target.value })} placeholder="EMP-001" />
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 32 }}>
                <button className="cd-modal-btn" style={{ flex: 1, background: '#f1f5f9', color: '#64748b' }} onClick={onClose}>Discard</button>
                <button className="cd-modal-btn" style={{ flex: 2, background: 'linear-gradient(135deg, #0f172a, #334155)', color: 'white', boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.3)' }} onClick={handleSaveLocal} disabled={uploading}>
                    {uploading ? 'Processing...' : data?._id ? 'Update Information' : 'Confirm Registration'}
                </button>
            </div>
        </ModalOverlay>
    );
}

function TripRequestSection({ client, routes, onComplete }) {
    const [reqType, setReqType] = useState('main'); // 'main' or 'adhoc'
    const [form, setForm] = useState({
        tripType: 'Login', tripFrequency: 'One Time',
        pickupLocation: '', dropLocation: '',
        tripDate: '', pickupTime: '', startTime: '', endTime: '',
        passengers: [{ passengerName: '', passengerPhone: '', passengerEmail: '' }],
        passengerName: '', passengerPhone: '',
        passengerEmail: '', routeId: '', recurringDays: []
    });
    const [routeSource, setRouteSource] = useState('pre'); // 'pre' or 'new'
    const [loading, setLoading] = useState(false);
    const pRef = useRef(); const dRef = useRef();

    // Reset form when switching tabs
    const switchTab = (tab) => {
        setReqType(tab);
        setForm({
            tripType: 'Login', tripFrequency: 'One Time',
            pickupLocation: '', dropLocation: '',
            tripDate: '', pickupTime: '', startTime: '', endTime: '',
            passengers: [{ passengerName: '', passengerPhone: '', passengerEmail: '' }],
            passengerName: '', passengerPhone: '',
            passengerEmail: '', routeId: '', recurringDays: []
        });
        setRouteSource('pre');
    };

    useEffect(() => {
        if (!window.google || (reqType === 'main' && routeSource === 'pre')) return;
        const pA = new window.google.maps.places.Autocomplete(pRef.current);
        const dA = new window.google.maps.places.Autocomplete(dRef.current);
        pA.addListener('place_changed', () => setForm(f => ({ ...f, pickupLocation: pA.getPlace().formatted_address })));
        dA.addListener('place_changed', () => setForm(f => ({ ...f, dropLocation: dA.getPlace().formatted_address })));
    }, [reqType, routeSource]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const isAdhoc = reqType === 'adhoc';
            const payload = {
                ...form,
                passengerName: isAdhoc ? form.passengers.map(p => p.passengerName).join(', ') : form.passengerName,
                passengerPhone: isAdhoc ? form.passengers.map(p => p.passengerPhone).join(', ') : form.passengerPhone,
                passengerEmail: isAdhoc ? form.passengers.map(p => p.passengerEmail).filter(Boolean).join(', ') : form.passengerEmail,
                operatorId: client.operatorId,
                clientId: client._id,
                status: 'Requested',
                tripType: isAdhoc ? 'on_demand' : form.tripType,
                startTime: isAdhoc ? form.pickupTime : form.startTime
            };

            if (reqType === 'main' && routeSource === 'new') {
                payload.routeId = null;
            }

            const res = await fetch(`${API}/trips`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                alert("Trip request transmitted successfully!");
                onComplete();
            } else {
                alert("Transmission failed. Please verify all data points.");
            }
        } catch (err) {
            console.error(err);
            alert("Network connectivity issue.");
        } finally {
            setLoading(false);
        }
    };

    const toggleDay = (day) => {
        const days = form.recurringDays.includes(day)
            ? form.recurringDays.filter(d => d !== day)
            : [...form.recurringDays, day];
        setForm({ ...form, recurringDays: days });
    };

    return (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
            {/* ── Tab Switcher ── */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, padding: 5, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <button
                    type="button"
                    onClick={() => switchTab('main')}
                    style={{
                        flex: 1, padding: '12px 20px', border: 'none', borderRadius: 10,
                        background: reqType === 'main' ? 'linear-gradient(135deg, #0f172a, #334155)' : 'transparent',
                        color: reqType === 'main' ? 'white' : '#64748b',
                        fontWeight: 700, fontSize: 14, cursor: 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: reqType === 'main' ? '0 4px 12px rgba(15,23,42,0.25)' : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                    }}
                >
                    <Icon d={icons.trips} size={16} color={reqType === 'main' ? 'white' : '#94a3b8'} />
                    Request Main Trip
                </button>
                <button
                    type="button"
                    onClick={() => switchTab('adhoc')}
                    style={{
                        flex: 1, padding: '12px 20px', border: 'none', borderRadius: 10,
                        background: reqType === 'adhoc' ? 'linear-gradient(135deg, #4f46e5, #6366f1)' : 'transparent',
                        color: reqType === 'adhoc' ? 'white' : '#64748b',
                        fontWeight: 700, fontSize: 14, cursor: 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: reqType === 'adhoc' ? '0 4px 12px rgba(99,102,241,0.3)' : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                    }}
                >
                    <Icon d={icons.plus} size={16} color={reqType === 'adhoc' ? 'white' : '#94a3b8'} />
                    Request Adhoc Trip
                </button>
            </div>

            <div className="cd-card">
                <div className="cd-card-header">
                    <Icon d={reqType === 'main' ? icons.trips : icons.plus} size={20} color={reqType === 'main' ? '#0f172a' : '#6366f1'} />
                    <span className="cd-card-title">
                        {reqType === 'main' ? 'New Main Schedule Request' : 'New On-Demand (Adhoc) Trip Request'}
                    </span>
                </div>

                {/* ── MAIN TRIP FORM ── */}
                {reqType === 'main' && (
                    <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                        <div style={{ gridColumn: 'span 2' }}>
                            <div style={{ background: '#f0f9ff', padding: '16px', borderRadius: '16px', border: '1px solid #bae6fd' }}>
                                <div style={{ fontSize: 11, fontWeight: 800, color: '#0369a1', textTransform: 'uppercase', marginBottom: 4 }}>Trip Modality</div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#0c4a6e' }}>Standard Operational Schedule (Recurring/Fixed)</div>
                            </div>
                        </div>
                        <div style={{ gridColumn: 'span 1' }}>
                            <label className="cd-req-label">Trip Flow Type</label>
                            <select className="cd-modal-input" value={form.tripType} onChange={e => setForm({ ...form, tripType: e.target.value })}>
                                <option value="Login">Login (Staff Pickup)</option>
                                <option value="Logout">Logout (Staff Drop)</option>
                            </select>
                        </div>
                        <div style={{ gridColumn: 'span 1' }}>
                            <label className="cd-req-label">Operational Frequency</label>
                            <select className="cd-modal-input" value={form.tripFrequency} onChange={e => setForm({ ...form, tripFrequency: e.target.value })}>
                                <option value="One Time">One Time (Fixed Date)</option>
                                <option value="Recurring">Recurring (Weekly Schedule)</option>
                            </select>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label className="cd-req-label">Operational Logistics Route</label>
                            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', color: routeSource === 'pre' ? '#0f172a' : '#64748b' }}>
                                    <input type="radio" name="routeSource" checked={routeSource === 'pre'} onChange={() => { setRouteSource('pre'); setForm(f => ({ ...f, routeId: '', pickupLocation: '', dropLocation: '' })); }} />
                                    Select Pre-Authorized
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', color: routeSource === 'new' ? '#0f172a' : '#64748b' }}>
                                    <input type="radio" name="routeSource" checked={routeSource === 'new'} onChange={() => { setRouteSource('new'); setForm(f => ({ ...f, routeId: '', pickupLocation: '', dropLocation: '' })); }} />
                                    Propose New Route
                                </label>
                            </div>

                            {routeSource === 'pre' ? (
                                <>
                                    <select className="cd-modal-input" value={form.routeId} onChange={e => { const r = routes.find(x => x._id === e.target.value); setForm({ ...form, routeId: e.target.value, pickupLocation: r?.pickupLocation || '', dropLocation: r?.dropLocation || '' }); }} required>
                                        <option value="">Select an authorized logistics route...</option>
                                        {routes.map(r => (
                                            <option key={r._id} value={r._id}>
                                                {r.name} ({r.distance}km) — {r.pickupLocation?.slice(0, 30)}... ➔ {r.dropLocation?.slice(0, 30)}...
                                            </option>
                                        ))}
                                    </select>
                                    {form.routeId && (
                                        <div style={{ marginTop: 12, padding: '12px 16px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}><span style={{ color: '#6366f1' }}>Pickup:</span> {form.pickupLocation}</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginTop: 4 }}><span style={{ color: '#6366f1' }}>Dropoff:</span> {form.dropLocation}</div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label className="cd-req-label" style={{ fontSize: 11 }}>New Pickup Location</label>
                                        <div className="cd-input-wrap"><Icon d={icons.map} size={16} color="#94a3b8" className="cd-input-icon" /><input ref={pRef} className="cd-modal-input-icon" value={form.pickupLocation} onChange={e => setForm({ ...form, pickupLocation: e.target.value })} placeholder="New pickup terminal" required /></div>
                                    </div>
                                    <div>
                                        <label className="cd-req-label" style={{ fontSize: 11 }}>New Drop Location</label>
                                        <div className="cd-input-wrap"><Icon d={icons.map} size={16} color="#94a3b8" className="cd-input-icon" /><input ref={dRef} className="cd-modal-input-icon" value={form.dropLocation} onChange={e => setForm({ ...form, dropLocation: e.target.value })} placeholder="New destination terminal" required /></div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {form.tripFrequency === 'One Time' ? (
                            <div style={{ gridColumn: 'span 2' }}>
                                <label className="cd-req-label">Specific Execution Date</label>
                                <input type="date" className="cd-modal-input" value={form.tripDate} onChange={e => setForm({ ...form, tripDate: e.target.value })} required />
                            </div>
                        ) : (
                            <div style={{ gridColumn: 'span 2' }}>
                                <label className="cd-req-label">Weekly Recurring Schedule</label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                        <button key={day} type="button" onClick={() => toggleDay(day)}
                                            style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: '1px solid #e2e8f0', background: form.recurringDays.includes(day) ? '#0f172a' : 'white', color: form.recurringDays.includes(day) ? 'white' : '#64748b', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                                            {day.charAt(0)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div style={{ gridColumn: 'span 1' }}>
                            <label className="cd-req-label">Schedule Start Time</label>
                            <input type="time" className="cd-modal-input" value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} required />
                        </div>
                        <div style={{ gridColumn: 'span 1' }}>
                            <label className="cd-req-label">Schedule End Time (Est.)</label>
                            <input type="time" className="cd-modal-input" value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} />
                        </div>

                        <div style={{ gridColumn: 'span 2', marginTop: 16 }}>
                            <button type="submit" disabled={loading} className="cd-add-btn" style={{ width: '100%', justifyContent: 'center', height: 60, fontSize: 17, background: 'linear-gradient(135deg, #0f172a, #334155)', color: 'white' }}>
                                {loading ? 'Transmitting Request...' : 'Transmit Main Trip Request to Operations'}
                            </button>
                            <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 12, fontWeight: 500 }}>
                                * Once transmitted, the operations team will assign a vehicle and driver for dispatch.
                            </p>
                        </div>
                    </form>
                )}

                {/* ── ADHOC TRIP FORM ── */}
                {reqType === 'adhoc' && (
                    <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                        <div style={{ gridColumn: 'span 2' }}>
                            <div style={{ background: '#faf5ff', padding: '16px', borderRadius: '16px', border: '1px solid #e9d5ff' }}>
                                <div style={{ fontSize: 11, fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', marginBottom: 4 }}>Trip Modality</div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#4c1d95' }}>On-Demand Adhoc Transport (Immediate / Unscheduled)</div>
                            </div>
                        </div>

                        {form.passengers.map((p, idx) => (
                            <div key={idx} style={{ gridColumn: 'span 2', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, padding: 16, background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0', position: 'relative' }}>
                                {idx > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, passengers: form.passengers.filter((_, i) => i !== idx) })}
                                        style={{ position: 'absolute', top: 10, right: 10, background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: 8, padding: '4px 8px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                    >✕ Remove</button>
                                )}
                                <div style={{ gridColumn: 'span 1' }}>
                                    <label className="cd-req-label">Passenger Name</label>
                                    <div className="cd-input-wrap">
                                        <Icon d={icons.user} size={16} color="#94a3b8" className="cd-input-icon" />
                                        <input className="cd-modal-input-icon" value={p.passengerName} onChange={e => { const newP = [...form.passengers]; newP[idx].passengerName = e.target.value; setForm({ ...form, passengers: newP }); }} placeholder="Full name of passenger" required />
                                    </div>
                                </div>
                                <div style={{ gridColumn: 'span 1' }}>
                                    <label className="cd-req-label">Passenger Phone</label>
                                    <div className="cd-input-wrap">
                                        <Icon d={icons.phone} size={16} color="#94a3b8" className="cd-input-icon" />
                                        <input className="cd-modal-input-icon" value={p.passengerPhone} onChange={e => { const newP = [...form.passengers]; newP[idx].passengerPhone = e.target.value; setForm({ ...form, passengers: newP }); }} placeholder="+971 XXXX XXXX" required />
                                    </div>
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label className="cd-req-label">Passenger Email (Optional)</label>
                                    <div className="cd-input-wrap">
                                        <Icon d={icons.mail} size={16} color="#94a3b8" className="cd-input-icon" />
                                        <input type="email" className="cd-modal-input-icon" value={p.passengerEmail} onChange={e => { const newP = [...form.passengers]; newP[idx].passengerEmail = e.target.value; setForm({ ...form, passengers: newP }); }} placeholder="passenger@company.com" />
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'center' }}>
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, passengers: [...form.passengers, { passengerName: '', passengerPhone: '', passengerEmail: '' }] })}
                                style={{ background: '#e0e7ff', color: '#4f46e5', border: '1px dashed #6366f1', borderRadius: 12, padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                            >+ Add Another Passenger</button>
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <label className="cd-req-label">Pickup Location</label>
                            <div className="cd-input-wrap">
                                <Icon d={icons.map} size={16} color="#94a3b8" className="cd-input-icon" />
                                <input ref={pRef} className="cd-modal-input-icon" value={form.pickupLocation} onChange={e => setForm({ ...form, pickupLocation: e.target.value })} placeholder="Where to pick up the passenger" required />
                            </div>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label className="cd-req-label">Drop Location</label>
                            <div className="cd-input-wrap">
                                <Icon d={icons.map} size={16} color="#94a3b8" className="cd-input-icon" />
                                <input ref={dRef} className="cd-modal-input-icon" value={form.dropLocation} onChange={e => setForm({ ...form, dropLocation: e.target.value })} placeholder="Destination / drop-off point" required />
                            </div>
                        </div>

                        <div style={{ gridColumn: 'span 1' }}>
                            <label className="cd-req-label">Trip Date</label>
                            <input type="date" className="cd-modal-input" value={form.tripDate} onChange={e => setForm({ ...form, tripDate: e.target.value })} required />
                        </div>
                        <div style={{ gridColumn: 'span 1' }}>
                            <label className="cd-req-label">Pickup Time</label>
                            <input type="time" className="cd-modal-input" value={form.pickupTime} onChange={e => setForm({ ...form, pickupTime: e.target.value })} required />
                        </div>

                        <div style={{ gridColumn: 'span 2', marginTop: 16 }}>
                            <button type="submit" disabled={loading} className="cd-add-btn" style={{ width: '100%', justifyContent: 'center', height: 60, fontSize: 17, background: 'linear-gradient(135deg, #4f46e5, #6366f1)', color: 'white' }}>
                                {loading ? 'Transmitting Adhoc Request...' : 'Submit Adhoc Trip Request'}
                            </button>
                            <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 12, fontWeight: 500 }}>
                                * Adhoc requests are fulfilled as on-demand transport — the operator will assign a vehicle promptly.
                            </p>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

function RoutesSection({ routes, loading }) {
    const [stops, setStops] = useState({});
    const [loadingStops, setLoadingStops] = useState(false);

    useEffect(() => {
        if (!routes.length) return;
        setLoadingStops(true);
        const fetchAllStops = async () => {
            const stopsData = {};
            await Promise.all(routes.map(async (r) => {
                try {
                    const res = await fetch(`${API}/route-stops/${r._id}`);
                    if (res.ok) {
                        const data = await res.json();
                        stopsData[r._id] = data;
                    }
                } catch (e) { console.error(e); }
            }));
            setStops(stopsData);
            setLoadingStops(false);
        };
        fetchAllStops();
    }, [routes]);

    return (
        <div className="cd-card">
            <div className="cd-card-header">
                <Icon d={icons.map} size={18} color="#6366f1" />
                <span className="cd-card-title">Assigned Logistics Routes</span>
            </div>
            {loading ? <div className="cd-empty">Loading Routes...</div> : routes.length === 0 ? <div className="cd-empty">No routes assigned yet.</div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="cd-table">
                        <thead>
                            <tr>
                                <th className="cd-th">Route Details</th>
                                <th className="cd-th">Pickup Hub</th>
                                <th className="cd-th">Sequence / Stops</th>
                                <th className="cd-th">Work Hub</th>
                                <th className="cd-th">Metrics</th>
                                <th className="cd-th">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {routes.map(r => (
                                <tr key={r._id} className="cd-tr">
                                    <td className="cd-td">
                                        <strong>{r.name}</strong>
                                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>ID: {r._id.slice(-8)}</div>
                                    </td>
                                    <td className="cd-td"><div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>{r.pickupLocation}</div></td>
                                    <td className="cd-td">
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {(stops[r._id] || []).length > 0 ? (
                                                stops[r._id].map((st, idx) => (
                                                    <div key={st._id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, background: '#f8fafc', padding: '4px 8px', borderRadius: 6, border: '1px solid #f1f5f9' }}>
                                                        <span style={{ fontWeight: 800, color: '#6366f1' }}>{idx + 1}</span>
                                                        <span style={{ color: '#1e293b' }}>{st.name}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Direct Route (No stops)</div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="cd-td"><div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>{r.dropLocation}</div></td>
                                    <td className="cd-td">
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1' }}>{r.distance ? `${r.distance} KM` : 'N/A'}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{r.estimatedMinutes ? `${r.estimatedMinutes} MINS` : '—'}</div>
                                    </td>
                                    <td className="cd-td">
                                        <span className="cd-badge" style={{ background: r.status === 'Active' ? '#dcfce7' : '#fee2e2', color: r.status === 'Active' ? '#166534' : '#ef4444' }}>
                                            {r.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function FormGroup({ label, value, onChange, type = 'text', options = [], innerRef }) {
    return (<div style={{ marginBottom: 12 }}><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>{label}</label>{type === 'select' ? (<select className="cd-modal-input" value={value} onChange={e => onChange(e.target.value)}>{options.map(o => <option key={o} value={o}>{o}</option>)}</select>) : (<input ref={innerRef} className="cd-modal-input" value={value} onChange={e => onChange(e.target.value)} />)}</div>);
}

function ModalOverlay({ children, onClose, title }) {
    return (
        <div className="cd-modal-overlay" onClick={onClose}>
            <div className="cd-modal-content" onClick={e => e.stopPropagation()}>
                {/* Sticky Header */}
                <div className="cd-modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 6, height: 24, background: 'linear-gradient(180deg, #6366f1, #0f172a)', borderRadius: 3 }} />
                        <h3 className="cd-modal-title">{title}</h3>
                    </div>
                    <button className="cd-modal-close" onClick={onClose}>
                        <Icon d={icons.x} size={16} color="#64748b" />
                    </button>
                </div>
                {/* Scrollable Body */}
                <div className="cd-modal-body">
                    {children}
                </div>
            </div>
        </div>
    );
}

function InfoRow({ icon, label, value }) {
    return (<div className="cd-info-row"><Icon d={icon} size={15} color="#6366f1" /><strong>{label}:</strong><span>{value || '—'}</span></div>);
}

function TripsSection({ trips, loading, client, onRefresh }) {
    const [filter, setFilter] = useState('All');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 10;

    const [suggestModal, setSuggestModal] = useState(null);
    const [clientNotes, setClientNotes] = useState('');
    const [responding, setResponding] = useState(false);

    const proposedTrips = trips.filter(t => t.status === 'PriceProposed');
    const filtered = trips.filter(t => filter === 'All' || t.status === filter);
    const scheduledCount = trips.filter(t => t.status === 'Scheduled').length;
    const requestedCount = trips.filter(t => t.status === 'Requested').length;
    const completedCount = trips.filter(t => t.status === 'Completed').length;
    const cancelledCount = trips.filter(t => t.status === 'Cancelled').length;

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const changeFilter = (f) => { setFilter(f); setPage(1); };

    const statusStyle = (s) => {
        const map = {
            'Scheduled': { bg: '#dcfce7', color: '#166534' },
            'Requested': { bg: '#fef3c7', color: '#92400e' },
            'Completed': { bg: '#dbeafe', color: '#1e40af' },
            'Cancelled': { bg: '#fee2e2', color: '#991b1b' },
            'Rejected': { bg: '#fee2e2', color: '#991b1b' },
            'In Progress': { bg: '#f0fdf4', color: '#166534' },
            'PriceProposed': { bg: '#ede9fe', color: '#5b21b6' },
            'ClientAccepted': { bg: '#d1fae5', color: '#065f46' },
            'ClientRejected': { bg: '#fee2e2', color: '#991b1b' },
            'ClientSuggested': { bg: '#fef9c3', color: '#713f12' },
        };
        return map[s] || { bg: '#f1f5f9', color: '#475569' };
    };

    const tripTypeStyle = (t) => {
        const lower = (t || '').toLowerCase();
        if (lower === 'login') return { bg: '#dbeafe', color: '#1d4ed8', label: '↗ Login' };
        if (lower === 'logout') return { bg: '#fce7f3', color: '#be185d', label: '↙ Logout' };
        if (lower.includes('on_demand') || lower.includes('adhoc')) return { bg: '#fef3c7', color: '#b45309', label: '⚡ Adhoc' };
        if (t) return { bg: '#f1f5f9', color: '#475569', label: t };
        return { bg: '#f1f5f9', color: '#94a3b8', label: '—' };
    };

    const statusLabel = (t) => {
        if (t.status === 'PriceProposed') return `💜 ${formatCurrency(t.proposedPrice || 0, client.country)}`;
        if (t.status === 'ClientAccepted') return '✅ Accepted';
        if (t.status === 'ClientRejected') return '✕ Rejected';
        if (t.status === 'ClientSuggested') return '💬 Counter Sent';
        if (t.status === 'In Progress') return '🚗 In Progress';
        return t.status || '—';
    };

    const handleClientResponse = async (tripId, responseStatus, notes = '') => {
        setResponding(true);
        try {
            await fetch(`${API}/trips/${tripId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status: responseStatus,
                    clientResponse: responseStatus === 'ClientAccepted' ? 'Accept' : responseStatus === 'ClientRejected' ? 'Reject' : 'Suggest',
                    clientNotes: notes
                })
            });
            setSuggestModal(null);
            setClientNotes('');
            if (onRefresh) onRefresh();
        } catch (err) { alert('Error responding. Please try again.'); }
        finally { setResponding(false); }
    };

    return (
        <div>
            {/* Price Proposal Cards */}
            {proposedTrips.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#5b21b6', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        💜 {proposedTrips.length} Price Proposal{proposedTrips.length > 1 ? 's' : ''} Awaiting Your Response
                    </div>
                    {proposedTrips.map(t => (
                        <div key={t._id} style={{ background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)', border: '2px solid #c4b5fd', borderRadius: 20, padding: '20px 24px', marginBottom: 14, boxShadow: '0 4px 20px rgba(139,92,246,0.12)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#1e1b4b', marginBottom: 4 }}>
                                        🚗 {t.passengerName || 'Trip Request'}
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed', marginLeft: 10, background: '#ede9fe', padding: '2px 8px', borderRadius: 20 }}>
                                            {t.tripDate ? new Date(t.tripDate).toLocaleDateString() : '—'} {t.pickupTime || t.startTime || ''}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#6d28d9', marginBottom: 6 }}>📍 {(t.pickupLocation || '').slice(0, 40)} → {(t.dropLocation || '').slice(0, 40)}</div>
                                    {t.operatorNotes && (
                                        <div style={{ fontSize: 12, color: '#7c3aed', background: '#ede9fe', padding: '6px 10px', borderRadius: 8, marginBottom: 8, fontStyle: 'italic' }}>📝 Operator Note: "{t.operatorNotes}"</div>
                                    )}
                                    <div style={{ fontSize: 22, fontWeight: 900, color: '#5b21b6' }}>
                                        {(t.proposedPrice > 0 || t.status === 'PriceProposed') ? `Proposed Price: ${formatCurrency(t.proposedPrice || 0, client.country)}` : 'Price Pending'}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 10, flexShrink: 0, alignItems: 'flex-start' }}>
                                    <button onClick={() => handleClientResponse(t._id, 'ClientAccepted')} disabled={responding} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>✅ Accept</button>
                                    <button onClick={() => { setSuggestModal(t); setClientNotes(''); }} disabled={responding} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>💬 Suggest</button>
                                    <button onClick={() => handleClientResponse(t._id, 'ClientRejected')} disabled={responding} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>✕ Reject</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Main Trips Table Card */}
            <div className="cd-card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Filter Bar */}
                <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {[
                            { label: 'All Trips', val: 'All', count: trips.length },
                            { label: 'Scheduled', val: 'Scheduled', count: scheduledCount },
                            { label: 'Requested', val: 'Requested', count: requestedCount },
                            { label: 'Completed', val: 'Completed', count: completedCount },
                            { label: 'Cancelled', val: 'Cancelled', count: cancelledCount },
                        ].map(({ label, val, count }) => (
                            <button key={val} onClick={() => changeFilter(val)} style={{
                                padding: '7px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                border: filter === val ? 'none' : '1.5px solid #e2e8f0',
                                background: filter === val ? '#0f172a' : 'white',
                                color: filter === val ? 'white' : '#475569',
                                transition: 'all 0.2s',
                                boxShadow: filter === val ? '0 4px 10px rgba(15,23,42,0.2)' : 'none'
                            }}>{label} <span style={{ opacity: 0.65 }}>({count})</span></button>
                        ))}
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="cd-empty" style={{ padding: '60px 24px' }}>Loading Trip Data...</div>
                ) : filtered.length === 0 ? (
                    <div className="cd-empty" style={{ padding: '60px 24px' }}>No trips found for this filter.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['Trip Type', 'Passenger', 'Date & Time', 'Route', 'Driver', 'Fleet / Vehicle', 'Status'].map(h => (
                                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pageData.map((t, i) => {
                                    const tt = tripTypeStyle(t.tripType);
                                    const ss = statusStyle(t.status);
                                    const driver = t.driverId;
                                    const vehicle = t.vehicleId;
                                    return (
                                        <tr key={t._id}
                                            style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc', cursor: 'default' }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                                            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}>

                                            {/* Trip Type */}
                                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                                                <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: tt.bg, color: tt.color }}>{tt.label}</span>
                                                {t.tripFrequency && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, fontWeight: 600 }}>{t.tripFrequency}</div>}
                                            </td>

                                            {/* Passenger */}
                                            <td style={{ padding: '14px 16px' }}>
                                                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>{t.passengerName || 'Multiple Passengers'}</div>
                                                {t.passengerPhone && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{t.passengerPhone}</div>}
                                            </td>

                                            {/* Date & Time */}
                                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                                                <div style={{ fontWeight: 700, fontSize: 13, color: '#6366f1' }}>
                                                    {t.tripDate ? new Date(t.tripDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                </div>
                                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontWeight: 600 }}>
                                                    {(t.pickupTime || t.startTime) ? `🕐 ${t.pickupTime || t.startTime}` : ''}
                                                    {t.endTime ? ` – ${t.endTime}` : ''}
                                                </div>
                                            </td>

                                            {/* Route */}
                                            <td style={{ padding: '14px 16px', maxWidth: 200 }}>
                                                <div style={{ fontSize: 11, fontWeight: 600, color: '#334155', lineHeight: 1.5 }}>
                                                    <span style={{ color: '#059669', fontWeight: 700 }}>FROM </span>{t.pickupLocation || '—'}
                                                </div>
                                                <div style={{ fontSize: 11, fontWeight: 600, color: '#334155', lineHeight: 1.5, marginTop: 3 }}>
                                                    <span style={{ color: '#e11d48', fontWeight: 700 }}>TO </span>{t.dropLocation || '—'}
                                                </div>
                                            </td>

                                            {/* Driver */}
                                            <td style={{ padding: '14px 16px' }}>
                                                {driver ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div style={{ width: 30, height: 30, borderRadius: 8, background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#4f46e5', flexShrink: 0 }}>
                                                            {(driver.name || '?').charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>{driver.name || '—'}</div>
                                                            {driver.phoneNumber && <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{driver.phoneNumber}</div>}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Pending dispatch</div>
                                                )}
                                            </td>

                                            {/* Fleet / Vehicle */}
                                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                                                {vehicle ? (
                                                    <div>
                                                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>🚘 {vehicle.plateNumber || vehicle.registrationNumber || '—'}</div>
                                                        {(vehicle.make || vehicle.model) && (
                                                            <div style={{ fontSize: 11, color: '#6366f1', marginTop: 2, fontWeight: 600 }}>
                                                                {[vehicle.make, vehicle.model].filter(Boolean).join(' ')}{vehicle.year ? ` (${vehicle.year})` : ''}
                                                            </div>
                                                        )}
                                                        {vehicle.vehicleType && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{vehicle.vehicleType}</div>}
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>TBA</div>
                                                )}
                                            </td>

                                            {/* Status */}
                                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                                                <span style={{ display: 'inline-block', padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: ss.bg, color: ss.color }}>
                                                    {statusLabel(t)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Page {page} of {totalPages} · {filtered.length} records</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                                style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: page === 1 ? '#f8fafc' : 'white', color: page === 1 ? '#cbd5e1' : '#334155', fontWeight: 700, cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}>
                                ← Prev
                            </button>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                                const p = start + i;
                                return p <= totalPages ? (
                                    <button key={p} onClick={() => setPage(p)}
                                        style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid', borderColor: p === page ? '#0f172a' : '#e2e8f0', background: p === page ? '#0f172a' : 'white', color: p === page ? 'white' : '#334155', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                                        {p}
                                    </button>
                                ) : null;
                            })}
                            <button disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: page === totalPages ? '#f8fafc' : 'white', color: page === totalPages ? '#cbd5e1' : '#334155', fontWeight: 700, cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13 }}>
                                Next →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Suggest / Counter-offer Modal */}
            {suggestModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                    <div style={{ background: '#fff', padding: '32px', borderRadius: '24px', width: '440px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 800, color: '#1e1b4b' }}>💬 Send a Suggestion</h3>
                        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>Share your counter-offer or notes. The operator will review and respond.</p>
                        <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Operator's Proposed Price:</div>
                            <div style={{ fontSize: '20px', fontWeight: 900, color: '#5b21b6' }}>{suggestModal.proposedPrice}</div>
                            {suggestModal.operatorNotes && <div style={{ fontSize: '12px', color: '#7c3aed', fontStyle: 'italic', marginTop: 4 }}>Note: "{suggestModal.operatorNotes}"</div>}
                        </div>
                        <textarea rows={4}
                            style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px', lineHeight: 1.6, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
                            placeholder="e.g. Can we do 150 instead? We have 3 passengers..."
                            value={clientNotes} onChange={e => setClientNotes(e.target.value)} />
                        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                            <button onClick={() => { setSuggestModal(null); setClientNotes(''); }} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#fff', fontWeight: 600, cursor: 'pointer', color: '#6b7280' }}>Cancel</button>
                            <button onClick={() => handleClientResponse(suggestModal._id, 'ClientSuggested', clientNotes)}
                                disabled={responding || !clientNotes.trim()}
                                style={{ flex: 2, padding: '12px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '14px', opacity: clientNotes.trim() ? 1 : 0.5 }}>
                                {responding ? 'Sending...' : '📨 Send Suggestion'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


function UsersSection({ client, users, loading, onRefresh }) {
    const [showModal, setShowModal] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'Transport Manager', status: 'Active' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (editUser) setForm({ ...editUser });
        else setForm({ name: '', email: '', password: '', role: 'Transport Manager', status: 'Active' });
    }, [editUser, showModal]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!editUser && users.length >= 2) {
            alert("Maximum limit of 2 users reached. Please remove an existing user to add a new one.");
            return;
        }
        setSaving(true);
        try {
            const url = editUser ? `${API}/client-users/${editUser._id}` : `${API}/client-users`;
            const method = editUser ? 'PUT' : 'POST';
            const payload = editUser ? form : { ...form, clientId: client._id, operatorId: client.operatorId };

            await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            setShowModal(false); onRefresh();
        } catch (err) { alert(err.message); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Remove this user's access?")) return;
        await fetch(`${API}/client-users/${id}`, { method: 'DELETE' });
        onRefresh();
    };

    return (
        <div className="cd-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div className="cd-card-title">Authorized Dashboard Users {users.length >= 2 && <span style={{ fontSize: 12, color: '#f59e0b', marginLeft: 10, fontWeight: 600 }}>(Limit Reached: 2 Users Max)</span>}</div>
                {users.length < 2 && (
                    <button onClick={() => { setEditUser(null); setShowModal(true); }} className="cd-add-btn">
                        <Icon d={icons.plus} size={18} /> Add New User
                    </button>
                )}
            </div>

            {loading ? <div className="cd-empty">Loading Users...</div> : users.length === 0 ? <div className="cd-empty">No additional users managed for this account.</div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="cd-table">
                        <thead>
                            <tr>
                                <th className="cd-th">User Information</th>
                                <th className="cd-th">Assigned Role</th>
                                <th className="cd-th">Account Status</th>
                                <th className="cd-th">Created On</th>
                                <th className="cd-th">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u._id} className="cd-tr">
                                    <td className="cd-td">
                                        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>{u.name}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{u.email}</div>
                                    </td>
                                    <td className="cd-td">
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#6366f1', background: '#eef2ff', padding: '4px 10px', borderRadius: 20, display: 'inline-block' }}>
                                            {u.role}
                                        </div>
                                    </td>
                                    <td className="cd-td">
                                        <span className="cd-badge" style={{ background: u.status === 'Active' ? '#dcfce7' : '#fee2e2', color: u.status === 'Active' ? '#166534' : '#991b1b' }}>
                                            {u.status}
                                        </span>
                                    </td>
                                    <td className="cd-td">{new Date(u.createdAt).toLocaleDateString()}</td>
                                    <td className="cd-td">
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button onClick={() => { setEditUser(u); setShowModal(true); }} className="cd-icon-btn"><Icon d={icons.analytics} size={14} /></button>
                                            <button onClick={() => handleDelete(u._id)} className="cd-icon-btn" style={{ color: '#ef4444' }}><Icon d={icons.logout} size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )
            }

            {
                showModal && (
                    <div className="cd-modal-overlay">
                        <div className="cd-modal-content">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>{editUser ? 'Edit Internal User' : 'Grant New Dashboard Access'}</h2>
                                <button onClick={() => setShowModal(false)} className="cd-icon-btn"><Icon d={icons.plus} size={20} style={{ transform: 'rotate(45deg)' }} /></button>
                            </div>
                            <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 10, textTransform: 'uppercase' }}>Full Name</label>
                                    <input className="cd-modal-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. John Doe" />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 10, textTransform: 'uppercase' }}>Email Address</label>
                                    <input type="email" className="cd-modal-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="name@company.com" />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 10, textTransform: 'uppercase' }}>Role/Designation</label>
                                    <select className="cd-modal-input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                                        <option value="Transport Manager">Transport Manager</option>
                                        <option value="Head of Business">Head of Business</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 10, textTransform: 'uppercase' }}>Account Password</label>
                                    <input type="text" className="cd-modal-input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editUser} placeholder="Set access password" />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 10, textTransform: 'uppercase' }}>Status</label>
                                    <select className="cd-modal-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                                <div style={{ gridColumn: 'span 2', marginTop: 16 }}>
                                    <button type="submit" disabled={saving} className="cd-add-btn" style={{ width: '100%', justifyContent: 'center', height: 56, fontSize: 16, background: '#0f172a', color: 'white' }}>
                                        {saving ? 'Updating Access...' : editUser ? 'Save Changes' : 'Grant Access'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
        </div >
    );
}

function FinanceSection({ invoices, trips, routes, country, loading }) {
    const [subTab, setSubTab] = useState('invoices'); // 'invoices' or 'services'
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('All');

    // Aggregate Invoice stats
    const totalPaid = invoices.filter(i => i.status === 'Paid').reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0);
    const totalPending = invoices.filter(i => i.status !== 'Paid').reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0);
    const totalInvoiced = invoices.reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0);

    // Group trips by Route for "Services" view
    const serviceStats = routes.map(r => {
        const routeTrips = trips.filter(t => (t.routeId?._id || t.routeId) === r._id);
        const lastTrip = routeTrips.length > 0 ? routeTrips.sort((a, b) => new Date(b.tripDate) - new Date(a.tripDate))[0] : null;
        return {
            ...r,
            tripCount: routeTrips.length,
            lastTripDate: lastTrip ? lastTrip.tripDate : null,
            completedCount: routeTrips.filter(t => t.status === 'Completed').length
        };
    }).filter(s => s.tripCount > 0); // Only show routes that have trips

    const filteredInvoices = invoices.filter(inv => {
        const matchesSearch = (inv.invoiceNumber || '').toLowerCase().includes(search.toLowerCase());
        const matchesFilter = filter === 'All' || inv.status === filter;
        return matchesSearch && matchesFilter;
    });

    return (
        <div className="cd-card" style={{ background: '#fcfcfc', border: '1px solid #e2e8f0' }}>
            {/* Distinct Financial Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, paddingBottom: 20, borderBottom: '2px solid #f1f5f9' }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: '#0f172a' }}>Financial Statement</h3>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13, fontWeight: 600 }}>Billing cycle and service consumption summary.</p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button
                        onClick={() => setSubTab('invoices')}
                        className={`cd-filter-btn ${subTab === 'invoices' ? "active" : ""}`} style={{ padding: '10px 24px', borderRadius: 12 }}
                    >
                        Raised Invoices
                    </button>
                    <button
                        onClick={() => setSubTab('services')}
                        className={`cd-filter-btn ${subTab === 'services' ? "active" : ""}`} style={{ padding: '10px 24px', borderRadius: 12 }}
                    >
                        Services Logged
                    </button>
                </div>
            </div >

            {/* Quick Metrics */}
            < div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 32 }}>
                <div style={{ background: 'white', padding: 20, borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>Total Lifecycle Value</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: '#1e293b' }}>{country || 'KWD'} {totalInvoiced.toLocaleString()}</div>
                </div>
                <div style={{ background: '#ecfdf5', padding: 20, borderRadius: 16, border: '1px solid #d1fae5', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#065f46', textTransform: 'uppercase', marginBottom: 8 }}>Settled Invoices</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: '#047857' }}>{country || 'KWD'} {totalPaid.toLocaleString()}</div>
                </div>
                <div style={{ background: '#fff7ed', padding: 20, borderRadius: 16, border: '1px solid #ffedd5', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', marginBottom: 8 }}>Current Arrears</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: '#c2410c' }}>{country || 'KWD'} {totalPending.toLocaleString()}</div>
                </div>
            </div >

            {subTab === 'invoices' ? (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                        <div style={{ display: 'flex', gap: 10 }}>
                            {['All', 'Paid', 'Unpaid', 'Overdue'].map(f => (
                                <button key={f} onClick={() => setFilter(f)} className={`cd-filter-btn ${filter === f ? "active" : ""}`} style={{ fontSize: 12, padding: '6px 16px' }}>{f}</button>
                            ))}
                        </div>
                        <div className="cd-search-wrap" style={{ width: 300 }}><Icon d={icons.search} size={14} color="#94a3b8" /><input className="cd-search-input" placeholder="Search Invoice #..." value={search} onChange={e => setSearch(e.target.value)} /></div>
                    </div >

                    {loading ? <div className="cd-empty">Retrieving Ledger...</div> : filteredInvoices.length === 0 ? <div className="cd-empty">No billing records discovered.</div> : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="cd-table">
                                <thead>
                                    <tr>
                                        <th className="cd-th">Document Reference</th>
                                        <th className="cd-th">Issue Date</th>
                                        <th className="cd-th">Maturity</th>
                                        <th className="cd-th">Gross Amount</th>
                                        <th className="cd-th">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredInvoices.map(inv => (
                                        <tr key={inv._id} className="cd-tr">
                                            <td className="cd-td">
                                                <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>{inv.invoiceNumber}</div>
                                                <div style={{ fontSize: 10, color: '#94a3b8', letterSpacing: '0.05em' }}>UID: {inv._id.slice(-8).toUpperCase()}</div>
                                            </td>
                                            <td className="cd-td">{inv.issueDate ? new Date(inv.issueDate).toLocaleDateString() : (inv.date ? new Date(inv.date).toLocaleDateString() : '—')}</td>
                                            <td className="cd-td">{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '—'}</td>
                                            <td className="cd-td">
                                                <div style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{country || 'KWD'} {Number(inv.totalAmount).toLocaleString()}</div>
                                            </td>
                                            <td className="cd-td">
                                                <span className="cd-badge" style={{
                                                    background: inv.status === 'Paid' ? '#dcfce7' : inv.status === 'Overdue' ? '#fee2e2' : '#f0f9ff',
                                                    color: inv.status === 'Paid' ? '#166534' : inv.status === 'Overdue' ? '#991b1b' : '#0369a1'
                                                }}>
                                                    {inv.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <p style={{ fontSize: 14, color: '#64748b', fontWeight: 600, marginBottom: 20 }}>Historical consumption summary of organized logistics routes (Services).</p>
                    {serviceStats.length === 0 ? <div className="cd-empty">No service activity logged yet.</div> : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="cd-table">
                                <thead>
                                    <tr>
                                        <th className="cd-th">Service Detail / Route</th>
                                        <th className="cd-th">Volume (Trips)</th>
                                        <th className="cd-th">Completed</th>
                                        <th className="cd-th">Last Deployment</th>
                                        <th className="cd-th">Allocation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {serviceStats.map(svc => (
                                        <tr key={svc._id} className="cd-tr">
                                            <td className="cd-td">
                                                <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>{svc.name}</div>
                                                <div style={{ fontSize: 11, color: '#64748b' }}>{svc.pickupLocation.slice(0, 20)}... ➔ {svc.dropLocation.slice(0, 20)}...</div>
                                            </td>
                                            <td className="cd-td">
                                                <div style={{ fontSize: 16, fontWeight: 900, color: '#6366f1' }}>{svc.tripCount}</div>
                                            </td>
                                            <td className="cd-td">
                                                <div style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>{svc.completedCount}</div>
                                            </td>
                                            <td className="cd-td">{svc.lastTripDate ? new Date(svc.lastTripDate).toLocaleDateString() : '—'}</td>
                                            <td className="cd-td">
                                                <div style={{ fontSize: 11, fontWeight: 700, color: '#334155', background: '#f1f5f9', padding: '4px 8px', borderRadius: 6, display: 'inline-block' }}>
                                                    {svc.distance ? `${svc.distance} KM` : 'Fixed Route'}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div >
    );
}

function QuotationsSection({ quotations, country, loading }) {
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('All');

    const filtered = quotations.filter(q => {
        const matchesSearch = (q.quotationNumber || '').toLowerCase().includes(search.toLowerCase());
        const matchesFilter = filter === 'All' || q.status === filter;
        return matchesSearch && matchesFilter;
    });

    return (
        <div className="cd-card" style={{ background: '#fcfcfc', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, paddingBottom: 20, borderBottom: '2px solid #f1f5f9' }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: '#0f172a' }}>Commercial Proposals</h3>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13, fontWeight: 600 }}>Review formal price quotations and service proposals.</p>
                </div>
                <div className="cd-search-wrap" style={{ width: 300 }}><Icon d={icons.search} size={14} color="#94a3b8" /><input className="cd-search-input" placeholder="Search Quotation #..." value={search} onChange={e => setSearch(e.target.value)} /></div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
                {['All', 'Draft', 'Sent', 'Accepted', 'Declined'].map(f => (
                    <button key={f} onClick={() => setFilter(f)} className={`cd-filter-btn ${filter === f ? "active" : ""}`} style={{ fontSize: 12, padding: '6px 16px' }}>{f}</button>
                ))}
            </div>

            {
                loading ? <div className="cd-empty">Retrieving Proposals...</div> : quotations.length === 0 ? <div className="cd-empty">No commercial proposals issued yet.</div> : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="cd-table">
                            <thead>
                                <tr>
                                    <th className="cd-th">Proposal Reference</th>
                                    <th className="cd-th">Issue Date</th>
                                    <th className="cd-th">Validity Date</th>
                                    <th className="cd-th">Quoted Amount</th>
                                    <th className="cd-th">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(q => (
                                    <tr key={q._id} className="cd-tr">
                                        <td className="cd-td">
                                            <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>{q.quotationNumber}</div>
                                            <div style={{ fontSize: 10, color: '#94a3b8', letterSpacing: '0.05em' }}>UID: {q._id.slice(-8).toUpperCase()}</div>
                                        </td>
                                        <td className="cd-td">{q.issueDate ? new Date(q.issueDate).toLocaleDateString() : '—'}</td>
                                        <td className="cd-td">{q.validUntil ? new Date(q.validUntil).toLocaleDateString() : '—'}</td>
                                        <td className="cd-td">
                                            <div style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{country || 'KWD'} {Number(q.totalAmount).toLocaleString()}</div>
                                        </td>
                                        <td className="cd-td">
                                            <span className="cd-badge" style={{
                                                background: q.status === 'Accepted' ? '#dcfce7' : q.status === 'Declined' ? '#fee2e2' : q.status === 'Sent' ? '#f0f9ff' : '#f1f5f9',
                                                color: q.status === 'Accepted' ? '#166534' : q.status === 'Declined' ? '#991b1b' : q.status === 'Sent' ? '#0369a1' : '#475569'
                                            }}>
                                                {q.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            }
        </div >
    );
}

function ReportsSection({ type, data, country }) {
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    const filtered = data.filter(item => {
        const itemDate = new Date(item.tripDate || item.issueDate);
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;

        if (from && itemDate < from) return false;
        if (to && itemDate > to) return false;
        if (statusFilter !== 'All' && item.status !== statusFilter) return false;
        return true;
    });

    const exportToCSV = () => {
        const header = type === 'trips' ? "Date,Reference,Amount/Category,Status\n" : "Date,Invoice#,Amount,Status\n";
        const rows = filtered.map(item => {
            const date = new Date(item.tripDate || item.issueDate).toLocaleDateString().replace(/,/g, '');
            const ref = (type === 'trips' ? (item.passengerName || item.routeName) : item.invoiceNumber).replace(/,/g, '');
            const amt = type === 'trips' ? 'Operational' : item.totalAmount;
            return `${date},${ref},${amt},${item.status}`;
        }).join("\n");

        const blob = new Blob([header + rows], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${type}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    return (
        <div className="cd-card">
            <div className="cd-card-header" style={{ border: 'none', marginBottom: 30 }}>
                <div style={{ flex: 1 }}>
                    <h3 className="cd-card-title">{type === 'trips' ? 'Trip Intensity Report' : 'Financial Activity Logs'}</h3>
                    <p style={{ fontSize: 13, color: '#666666', marginTop: 4 }}>Detailed operational breakdown and lifecycle tracking.</p>
                </div>
                <button className="cd-add-btn" onClick={exportToCSV}>
                    <Icon d={icons.download} size={16} /> <span>Export Data</span>
                </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 40, background: '#f8fafc', padding: 24, borderRadius: 20, border: '1px solid #e2e8f0' }}>
                <div style={{ flex: '1 1 200px' }}>
                    <label className="cd-req-label">Period From</label>
                    <input type="date" className="cd-modal-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                    <label className="cd-req-label">Period To</label>
                    <input type="date" className="cd-modal-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                    <label className="cd-req-label">Status Filter</label>
                    <select className="cd-modal-input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                        <option value="All">All Activity</option>
                        {type === 'trips' ? (
                            <>
                                <option value="Scheduled">Scheduled</option>
                                <option value="Completed">Completed</option>
                                <option value="Requested">Requested</option>
                                <option value="Rejected">Rejected</option>
                            </>
                        ) : (
                            <>
                                <option value="Paid">Paid</option>
                                <option value="Unpaid">Unpaid</option>
                                <option value="Overdue">Overdue</option>
                            </>
                        )}
                    </select>
                </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
                <table className="cd-table">
                    <thead>
                        <tr>
                            <th className="cd-th">Date</th>
                            <th className="cd-th">Document / Reference</th>
                            <th className="cd-th">Value</th>
                            <th className="cd-th">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan="4" className="cd-empty">No data points found for selected filters.</td></tr>
                        ) : filtered.map((item, idx) => (
                            <tr key={idx} className="cd-tr">
                                <td className="cd-td">{new Date(item.tripDate || item.issueDate).toLocaleDateString()}</td>
                                <td className="cd-td">
                                    <div style={{ fontWeight: 800, color: '#0f172a' }}>{type === 'trips' ? (item.passengerName || item.routeName) : item.invoiceNumber}</div>
                                    <div style={{ fontSize: 10, color: '#94a3b8', letterSpacing: '0.05em' }}>UID: {item._id.slice(-8).toUpperCase()}</div>
                                </td>
                                <td className="cd-td">
                                    {type === 'trips' ? (
                                        <span style={{ color: '#64748b', fontSize: 13 }}>Operational Trip</span>
                                    ) : (
                                        <div style={{ fontWeight: 800, color: '#0f172a' }}>{country || 'KWD'} {Number(item.totalAmount).toLocaleString()}</div>
                                    )}
                                </td>
                                <td className="cd-td">
                                    <span className="cd-badge" style={{
                                        background: ['Completed', 'Paid'].includes(item.status) ? '#dcfce7' : ['Overdue', 'Rejected', 'Cancelled'].includes(item.status) ? '#fee2e2' : '#eff6ff',
                                        color: ['Completed', 'Paid'].includes(item.status) ? '#15803d' : ['Overdue', 'Rejected', 'Cancelled'].includes(item.status) ? '#b91c1c' : '#1d4ed8'
                                    }}>
                                        {item.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

